import { OTB_EDITABLE_FIELDS } from '../../../components/components.constants'

const isMissing = (value) => value == null || value === 0;

const recalcBaseMonth = (monthData, editedField, newValue) => {
  let { grossRevenue, originalPrice, unitsSold } = monthData;

  if (editedField === OTB_EDITABLE_FIELDS.PRECO_MEDIO) {
    originalPrice = newValue;
    unitsSold = !isMissing(grossRevenue) && !isMissing(originalPrice)
      ? Math.floor(grossRevenue / originalPrice)
      : unitsSold;
  }

  else if (editedField === OTB_EDITABLE_FIELDS.FATURAMENTO) {
    grossRevenue = newValue;
    unitsSold = !isMissing(originalPrice) && !isMissing(grossRevenue)
      ? Math.floor(grossRevenue / originalPrice)
      : unitsSold;
  }

  else if (editedField === OTB_EDITABLE_FIELDS.PECAS_VENDIDAS) {
    unitsSold = Math.floor(newValue);
    grossRevenue = !isMissing(originalPrice) && !isMissing(unitsSold)
      ? originalPrice * unitsSold
      : grossRevenue;
  }

  else {
    throw new Error(`Campo "${editedField}" não é um campo editável de Base.`);
  }

  return { ...monthData, grossRevenue, originalPrice, unitsSold, plannedUnitsSold: unitsSold, editedBase: true };
};

const recalcCostMargin = (monthData) => {
  const { originalPrice, unitCost, grossRevenue, unitsSold } = monthData;

  if (isMissing(unitCost) || isMissing(originalPrice)) return monthData;

  const salePrice = originalPrice;
  const markdownPercent = 0;
  const initialMarkup = (originalPrice - unitCost) / unitCost * 100;
  const finalMarkup = initialMarkup;
  const grossProfit = grossRevenue - unitCost * unitsSold;

  return {
    ...monthData,
    salePrice,
    markdownPercent,
    initialMarkup,
    finalMarkup,
    grossProfit,
  };
};

const normalizeMonth = (m) => ((m - 1) % 12 + 12) % 12 + 1;

const salesUnitsOf = (month) => month?.plannedUnitsSold ?? month?.unitsSold ?? 0;

const computeCoverageUnits = (monthsData, index, coverageTarget) => {
  const fullMonths = Math.floor(coverageTarget);
  const fraction = coverageTarget - fullMonths;

  let total = 0;
  for (let i = 1; i <= fullMonths; i++) {
    const month = monthsData[index + i];
    if (!month) break;
    total += salesUnitsOf(month);
  }

  const fractionalMonth = monthsData[index + fullMonths + 1];
  if (fractionalMonth && fraction > 0) {
    total += fraction * salesUnitsOf(fractionalMonth);
  }

  return total;
};

const computeActualCoverage = (monthsData, index, currentMonth) => {
  const month = monthsData[index];
  const startingStock = month.month === currentMonth
    ? month.initialInventoryUnits
    : month.endingInventoryUnits;

  if (startingStock == null || startingStock <= 0) return 0;

  const plannedByMonth = {};
  monthsData.forEach((m) => {
    plannedByMonth[m.month] = salesUnitsOf(m);
  });

  let remaining = startingStock;
  let coverage = 0;
  let calendarMonth = month.month;

  for (let i = 0; i < 24 && remaining > 0; i++) {
    const planned = plannedByMonth[calendarMonth] ?? 0;
    if (planned > 0) {
      if (remaining >= planned) {
        remaining -= planned;
        coverage += 1;
      } else {
        coverage += remaining / planned;
        break;
      }
    }
    calendarMonth = normalizeMonth(calendarMonth + 1);
  }

  return coverage;
};

export const resolveRecomputeFrom = ({ monthsData, anchorIndex, coverageTarget }) => {
  const now = new Date();
  const currentKey = now.getFullYear() * 12 + now.getMonth();
  const foundIndex = monthsData.findIndex((m) => m.year * 12 + (m.month - 1) >= currentKey);
  const currentIndex = foundIndex === -1 ? anchorIndex : foundIndex;

  return Math.max(currentIndex, anchorIndex - Math.ceil(coverageTarget), 0);
};

const projectInventoryForward = (monthsData, fromIndex, coverageTarget, currentMonth, includePurchase = true) => {
  const result = [...monthsData];

  for (let i = fromIndex; i < result.length; i++) {
    const month = result[i];

    const estoqueInicial = i === fromIndex
      ? month.initialInventoryUnits
      : result[i - 1].endingInventoryUnits;

    const vendas = salesUnitsOf(month);
    const carteira = month.inboundInventoryUnits ?? 0;

    const idealStock = computeCoverageUnits(result, i, coverageTarget) + (month.presentationStockUnits ?? 0);
    const estoqueFinalSemCompra = (estoqueInicial ?? 0) - vendas + carteira;
    const compra = Math.max(0, idealStock - estoqueFinalSemCompra);
    const estoqueFinal = includePurchase ? estoqueFinalSemCompra + compra : estoqueFinalSemCompra;

    result[i] = {
      ...month,
      initialInventoryUnits: estoqueInicial,
      endingInventoryUnits: estoqueFinal,
      idealInventoryUnits: idealStock,
      openToBuyUnits: compra,
      grossOpenToBuyUnits: compra,
      plannedInventoryCoverage: coverageTarget,
    };

    result[i].actualInventoryCoverage = Math.round(computeActualCoverage(result, i, currentMonth) * 100) / 100;
  }

  return result;
};

export const applyOtbEdit = ({ monthsData, anchorIndex, editedField, newValue, coverageTarget, currentMonth = new Date().getMonth() + 1, includePurchase = true }) => {
  if (anchorIndex < 0 || anchorIndex >= monthsData.length) {
    throw new Error('anchorIndex fora do range de monthsData.');
  }

  const result = [...monthsData];
  result[anchorIndex] = recalcBaseMonth(result[anchorIndex], editedField, newValue);
  result[anchorIndex] = recalcCostMargin(result[anchorIndex]);

  const recomputeFrom = resolveRecomputeFrom({ monthsData: result, anchorIndex, coverageTarget });

  return projectInventoryForward(result, recomputeFrom, coverageTarget, currentMonth, includePurchase);
};